/**
 * CustomerInquiriesPanel — Vendor / Venue / Pro dashboards see all
 * customer cart items containing their service, with customer phone +
 * a "Mark Booked" button. Mirrors the customer-side action.
 *
 * Backend:
 *   GET  /api/me/cart-mentions
 *   POST /api/me/cart-mentions/{package_id}/{item_index}/booked
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShoppingCart, Phone, Calendar, MapPin, CheckCircle, Spinner, Info,
} from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

const fmtINR = (n) => (n === null || n === undefined) ? '—'
  : `₹${Number(n).toLocaleString('en-IN')}`;

const CustomerInquiriesPanel = () => {
  const [loading, setLoading] = useState(true);
  const [mentions, setMentions] = useState([]);
  const [busyKey, setBusyKey] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/me/cart-mentions`, { withCredentials: true });
      setMentions(data?.mentions || []);
    } catch (err) {
      if (err?.response?.status === 403) {
        // not eligible — silently hide
        setMentions([]);
      } else {
        toast.error(err?.response?.data?.detail || 'Could not load inquiries');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const markBooked = async (m) => {
    const key = `${m.package_id}:${m.item_index}`;
    setBusyKey(key);
    try {
      await axios.post(
        `${BACKEND_URL}/api/me/cart-mentions/${m.package_id}/${m.item_index}/booked`,
        {},
        { withCredentials: true },
      );
      toast.success(`Marked booked for ${m.customer_name || 'customer'}`);
      // Optimistic remove from the list (server hides booked items on next refresh)
      setMentions((prev) => prev.filter((x) =>
        !(x.package_id === m.package_id && x.item_index === m.item_index)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not mark booked');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="customer-inquiries-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <ShoppingCart size={20} className="text-emerald-700" weight="duotone" /> Customer Inquiries
          {mentions.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 bg-emerald-600 text-white rounded font-bold uppercase tracking-wider"
              data-testid="customer-inquiries-count">
              {mentions.length}
            </span>
          )}
        </CardTitle>
        <CardDescription>
          Every customer who saved your service in their cart shows up here with their phone
          number. Reach out, agree on terms, and tap <strong>Mark Booked</strong> to lock the date
          on your calendar.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-text-secondary text-sm py-4">
            <Spinner size={16} className="animate-spin" /> Loading inquiries…
          </div>
        ) : mentions.length === 0 ? (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded">
            <Info size={16} className="text-amber-700 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">
              No customer cart inquiries yet. As soon as a customer adds your service to their
              estimate and saves it, you&apos;ll see their phone here.
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="customer-inquiries-list">
            {mentions.map((m) => {
              const key = `${m.package_id}:${m.item_index}`;
              const busy = busyKey === key;
              return (
                <li
                  key={key}
                  data-testid={`customer-inquiry-${m.package_id}-${m.item_index}`}
                  className="border border-[#E8E1D5] rounded p-3 flex flex-col md:flex-row md:items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs px-1.5 py-0.5 bg-[#F7F2EA] text-text-secondary border border-[#E8E1D5] rounded-sm uppercase tracking-wider font-bold">
                        {m.event_type || 'Event'}
                      </span>
                      <span className="text-xs text-text-secondary flex items-center gap-1">
                        <Calendar size={12} /> {fmtDate(m.event_date)}
                      </span>
                      {m.event_city && (
                        <span className="text-xs text-text-secondary flex items-center gap-1">
                          <MapPin size={12} /> {m.event_city}
                        </span>
                      )}
                      {m.guests ? (
                        <span className="text-xs text-text-secondary">{m.guests} guests</span>
                      ) : null}
                    </div>
                    <div className="text-sm font-bold text-text-primary truncate">
                      {m.item_name}
                      {m.item_category && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-text-secondary font-normal">
                          {m.item_category}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-text-secondary mt-0.5">
                      Quoted: <strong>{fmtINR(m.item_price)}</strong>
                      {m.item_qty > 1 ? <span className="ml-1">× {m.item_qty}</span> : null}
                      <span className="mx-2">·</span>
                      Saved as: <em>{m.package_name}</em>
                    </div>
                  </div>

                  <div className="flex flex-col md:items-end gap-2 md:min-w-[200px]">
                    {m.customer_phone ? (
                      <a
                        href={`tel:${m.customer_phone}`}
                        className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-primary hover:underline"
                        data-testid={`customer-inquiry-phone-${m.package_id}-${m.item_index}`}
                      >
                        <Phone size={14} /> {m.customer_phone}
                      </a>
                    ) : (
                      <span className="text-xs text-text-secondary italic">No phone</span>
                    )}
                    <Button
                      size="sm"
                      onClick={() => markBooked(m)}
                      disabled={busy}
                      className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`mark-booked-btn-${m.package_id}-${m.item_index}`}
                    >
                      {busy
                        ? <><Spinner size={14} className="mr-2 animate-spin" /> Marking…</>
                        : <><CheckCircle size={14} className="mr-2" weight="fill" /> Mark Booked</>}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomerInquiriesPanel;
